/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormDialog, FormRow } from '../../../ui';
import { InputForm } from '../InputForm';
import { MaskedInputForm } from '../MaskedInputForm';
import type { NewResponsible } from './index';

interface NewResponsibleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: NewResponsible) => void;
  initialValue?: NewResponsible | null;
  defaultFunction: string;
  entityLabel: string;
}

interface DialogValues {
  responsibleName: string;
  responsibleEmail: string;
  responsiblePhone: string;
  responsibleFunction: string;
}

const schema = Yup.object().shape({
  responsibleName: Yup.string().required('Este campo é obrigatório!'),
  responsibleEmail: Yup.string().email('Email inválido').required('Este campo é obrigatório!'),
  responsiblePhone: Yup.string().required('Este campo é obrigatório!'),
  responsibleFunction: Yup.string().required('Este campo é obrigatório!'),
});

const toDialogValues = (value: NewResponsible | null | undefined, fn: string): DialogValues => ({
  responsibleName: value?.name ?? '',
  responsibleEmail: value?.email ?? '',
  responsiblePhone: value?.phone ?? '',
  responsibleFunction: value?.function ?? fn,
});

export const NewResponsibleDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialValue,
  defaultFunction,
  entityLabel,
}: NewResponsibleDialogProps) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DialogValues>({
    resolver: yupResolver(schema),
    defaultValues: toDialogValues(null, defaultFunction),
  });

  useEffect(() => {
    if (isOpen) reset(toDialogValues(initialValue, defaultFunction));
  }, [isOpen, initialValue, defaultFunction, reset]);

  return (
    <FormDialog
      isOpen={isOpen}
      onClose={onClose}
      title={initialValue ? 'Editar responsável' : 'Novo responsável'}
      description={`Os dados abaixo criam um novo usuário responsável pela ${entityLabel}. Ele receberá a senha padrão no primeiro acesso.`}
      onSubmit={(event) => {
        event.stopPropagation();
        handleSubmit((values) => {
          onSubmit({
            name: values.responsibleName,
            email: values.responsibleEmail,
            phone: values.responsiblePhone,
            function: values.responsibleFunction,
          });
          onClose();
        })(event);
      }}
      submitLabel={initialValue ? 'Salvar alterações' : 'Adicionar responsável'}
    >
      <FormRow>
        <InputForm
          name="responsibleName"
          label="Nome"
          placeholder="Nome do responsável"
          isRequired
          register={register as any}
          errors={errors}
        />
        <InputForm
          name="responsibleEmail"
          label="Email"
          type="email"
          placeholder="email@exemplo.com"
          isRequired
          register={register as any}
          errors={errors}
        />
      </FormRow>
      <FormRow>
        <MaskedInputForm
          name="responsiblePhone"
          label="Telefone"
          placeholder="(99) 99999-9999"
          mask="(99) 99999-9999"
          isRequired
          control={control}
          errors={errors}
        />
        <InputForm
          name="responsibleFunction"
          label="Função"
          isRequired
          isReadOnly
          register={register as any}
          errors={errors}
        />
      </FormRow>
    </FormDialog>
  );
};
